import { useMemo, useState } from "react";
import { formatRelativeTime } from "../../../utils/dateFormat";
import { Button } from "../../Button/Button";
import { Icon } from "../../Icons/Icons";
import { LoadingSpinner } from "../../LoadingSpinner/LoadingSpinner";
import { TimelineItemData } from "../../TimelineItem/types";
import { Body } from "../../Typography/Body";
import {
  computeChangesCounts,
  formatChangesSummary,
} from "../utils/computeChangesCounts";

export interface TimelineBulkActionsFooterProps<T extends string = string> {
  /**
   * Timeline items for computing change counts
   */
  items: TimelineItemData<T>[];

  /**
   * Whether changes are currently syncing
   */
  isSyncing: boolean;

  /**
   * ISO timestamp of most recent edit
   */
  lastEditedAt: string | null | undefined;

  /**
   * ISO timestamp of last publish
   */
  lastPublishedAt: string | null | undefined;

  /**
   * Count of created drafts (locally and server-side)
   * Used to show accurate edit counts in the bulk actions footer
   */
  totalDraftsCount: number;

  /**
   * Currently selected item IDs
   */
  selectedItems: Set<string>;

  /**
   * Callback to clear selections
   */
  onClearSelections: () => void;

  /**
   * Callback for bulk discard changes
   */
  onBulkDiscardChanges?: (itemIds: string[]) => Promise<void>;

  /**
   * Callback for bulk delete
   */
  onBulkDelete?: (itemIds: string[]) => Promise<void>;

  /**
   * Callback to compare the selected entries side by side. The button only
   * renders from two selections up — a single entry has nothing to compare
   * against, and offering it there would read as a view action rather than a
   * comparison.
   */
  onCompare?: (itemIds: string[]) => void;

  /**
   * Current visible row number (1-indexed)
   * Used to show position indicator in the footer
   */
  currentRowNumber?: number;

  /**
   * Total number of filtered items in the list
   * Used to show position indicator in the footer
   */
  totalItems?: number;
}

/**
 * Footer component for Timeline edit mode bulk actions.
 * Displays sync status, edit timestamp with change summary, and bulk action buttons.
 */
export const TimelineBulkActionsFooter = <T extends string = string>({
  items,
  isSyncing,
  lastEditedAt,
  lastPublishedAt,
  totalDraftsCount,
  selectedItems,
  onClearSelections,
  onBulkDiscardChanges,
  onBulkDelete,
  onCompare,
  currentRowNumber,
  totalItems,
}: TimelineBulkActionsFooterProps<T>) => {
  const [bulkActionInProgress, setBulkActionInProgress] = useState<
    "discard" | "delete" | null
  >(null);

  // Compute change counts from items (added/deleted from flags)
  const itemBasedCounts = useMemo(() => computeChangesCounts(items), [items]);

  // Combine item-based counts with totalDraftsCount for accurate "edited" count
  // totalDraftsCount tracks edits that haven't been reflected in server data yet
  const changesCounts = useMemo(
    () => ({
      added: itemBasedCounts.added,
      edited: totalDraftsCount,
      deleted: itemBasedCounts.deleted,
    }),
    [itemBasedCounts, totalDraftsCount],
  );

  const changesSummary = useMemo(
    () => formatChangesSummary(changesCounts),
    [changesCounts],
  );

  // Server-side pending changes (based on timestamps from briefing data)
  const hasServerPendingChanges =
    lastEditedAt && lastEditedAt !== lastPublishedAt;

  // Local pending changes (has any additions, edits, or deletions)
  const hasLocalChanges = changesSummary !== "";

  return (
    <div className="indigo-timeline__bulk-actions">
      {/* Status section - left aligned */}
      {isSyncing ? (
        <div className="flex items-center gap-2 mr-auto">
          <LoadingSpinner
            size={16}
            className="text-indigo-text-tertiary"
            ariaLabel="Syncing changes"
          />
          <Body size="sm">Syncing changes</Body>
        </div>
      ) : hasServerPendingChanges ? (
        <div className="flex items-center gap-2 mr-auto">
          <Body size="sm" className="text-indigo-text-tertiary">
            Last edited: {formatRelativeTime(lastEditedAt)}
            {changesSummary && (
              <>
                <br />
                {`Draft changes: ${changesSummary}`}
              </>
            )}
          </Body>
        </div>
      ) : hasLocalChanges ? (
        // Show changes summary even without server timestamp (for local edits before draft creation)
        <div className="flex items-center gap-2 mr-auto">
          <Body size="sm" className="text-indigo-text-tertiary">
            {changesSummary}
          </Body>
        </div>
      ) : lastPublishedAt ? (
        <div className="flex items-center gap-2 mr-auto">
          <Body size="sm" className="text-indigo-text-tertiary">
            Last published: {formatRelativeTime(lastPublishedAt)}
          </Body>
        </div>
      ) : null}

      {/* Position indicator - right aligned */}
      {currentRowNumber !== undefined &&
        totalItems !== undefined &&
        !selectedItems.size && (
          <Body
            size="sm"
            className="text-indigo-text-tertiary ml-auto tabular-nums"
          >
            {currentRowNumber} / {totalItems}
          </Body>
        )}

      {/* Bulk action buttons - right aligned */}
      {selectedItems.size > 0 && (
        <>
          <Button
            variant="ghost"
            size="md"
            icon={<Icon name="close" />}
            disabled={bulkActionInProgress !== null}
            onClick={onClearSelections}
            aria-label={`Clear ${selectedItems.size} selected ${selectedItems.size === 1 ? "item" : "items"}`}
          >
            Clear {selectedItems.size === 1 ? "selection" : "selections"}
          </Button>
          {onCompare && selectedItems.size > 1 && (
            <Button
              variant="ghost"
              size="md"
              icon={<Icon name="browser" />}
              disabled={bulkActionInProgress !== null}
              onClick={() => onCompare(Array.from(selectedItems))}
            >
              Compare {selectedItems.size} entries
            </Button>
          )}
          <Button
            variant="ghost"
            size="md"
            icon={<Icon name="refresh" />}
            disabled={selectedItems.size === 0 || bulkActionInProgress !== null}
            loading={bulkActionInProgress === "discard"}
            onClick={() => {
              if (onBulkDiscardChanges && selectedItems.size > 0) {
                setBulkActionInProgress("discard");
                void onBulkDiscardChanges(Array.from(selectedItems)).finally(
                  () => {
                    setBulkActionInProgress(null);
                    onClearSelections();
                  },
                );
              }
            }}
          >
            Discard changes for {selectedItems.size}{" "}
            {selectedItems.size === 1 ? "entry" : "entries"}
          </Button>
          <Button
            variant="danger"
            size="md"
            icon={<Icon name="trash" />}
            disabled={selectedItems.size === 0 || bulkActionInProgress !== null}
            loading={bulkActionInProgress === "delete"}
            onClick={() => {
              if (onBulkDelete && selectedItems.size > 0) {
                setBulkActionInProgress("delete");
                const itemsToDelete = Array.from(selectedItems);
                void onBulkDelete(itemsToDelete).finally(() => {
                  setBulkActionInProgress(null);
                  onClearSelections();
                });
              }
            }}
          >
            Delete {selectedItems.size}{" "}
            {selectedItems.size === 1 ? "entry" : "entries"}
          </Button>
        </>
      )}
    </div>
  );
};
